import {
  ComponentProps,
  CSSProperties,
  FC,
  memo,
  MouseEventHandler,
  RefObject,
  Suspense,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import {
  CUSTOM_CATEGORIES,
  CUSTOM_CATEGORIES_ARRAY,
  CUSTOM_CATEGORY_IDS_WITHOUT_PROVIDERS
} from 'utils/constants/casino/custom-categories';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy as namedLazy } from 'utils/generic/lazy';
import { mountPoint, shouldRedirect } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { NewCasinoGame } from 'interfaces/new-casino';
import { CasinoGameCard as CasinoGameCardComponent } from 'newcomponents/Shared/casino/CasinoGameCard';
import { PersonalizedGameCard } from 'newcomponents/UI/UserPersonalization/__partials/Steps/SelectCasinoGames/PersonalizedGameCard';
// import { useClickOutside } from 'hooks/useClickOutside';
import { clearAllFavorite } from 'store/actions';
// import { setMobileActiveCardID } from 'store/actions/new-casino';
import { getFavoriteProviders } from 'store/selectors/fav-data';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import {
  CasinoGamesLayoutType,
  CasinoGamesLoadMoreBtnType,
  useCasinoGamesConfigsValues
} from '../../providers/CasinoGamesConfigsContext';
import { CasinoGamesCarousel } from '../GamesCarousel';
import { GamesLazyLoad } from '../GamesLazyLoad';
import './index.less';

const { CasinoLoadMoreButton } = namedLazy(
  /* webpackChunkName: "CasinoLoadMoreButton" */ () =>
    import('../LoadMoreButton')
);

interface GamesByCategoryProps {
  category: {
    id: string;
    name: string;
    gamesCount: number;
  };
  data: NewCasinoGame[];
  parentRef: RefObject<HTMLDivElement>;
}
const MIN_GAMES_PER_ROW = 4;

const _GamesByCategory: FC<GamesByCategoryProps> = ({
  category,
  data,
  parentRef
}) => {
  const { configs, computedConfigs } = useCasinoGamesConfigsValues();
  const favoriteProviders = useSelector(getFavoriteProviders) || [];

  const { selectedCategory } = useSelectedCategory({ configs });
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const history = useHistory();
  const { location } = history;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // const setMobileActiveCardCLickHandler = useCallback(
  //   (id: null | string = null) => {
  //     isMobile() && dispatch(setMobileActiveCardID(id));
  //   },
  //   []
  // );
  //
  // useClickOutside(containerRef, () => {
  //   isMobile() && setMobileActiveCardCLickHandler(null);
  // });

  const isLoadMoreButtonSlider =
    computedConfigs.loadMoreButtonType === CasinoGamesLoadMoreBtnType.SLIDER;

  const showLazyViewOnSingleCategory =
    selectedCategory !== CUSTOM_CATEGORIES.all.id &&
    isLoadMoreButtonSlider &&
    !!configs.groupAllByCategories &&
    isMobile();

  const isLoadMoreButtonLazyLoad =
    showLazyViewOnSingleCategory ||
    computedConfigs.loadMoreButtonType === CasinoGamesLoadMoreBtnType.LAZY_LOAD;

  const customCategory = {
    isSelected:
      CUSTOM_CATEGORY_IDS_WITHOUT_PROVIDERS.includes(selectedCategory),
    category: CUSTOM_CATEGORIES_ARRAY.find(
      _customCategory => _customCategory.id === selectedCategory
    )
  };

  const viewAllEnabled =
    selectedCategory === CUSTOM_CATEGORIES.all.id &&
    !!configs.groupAllByCategories;

  const isPromotedCategory = [
    CUSTOM_CATEGORIES.special.id,
    CUSTOM_CATEGORIES.specialLive.id
  ].includes(selectedCategory);

  const loadMoreFunctionalityAvailable =
    computedConfigs.loadMoreButtonEnabled &&
    (selectedCategory !== CUSTOM_CATEGORIES.all.id ||
      (selectedCategory === CUSTOM_CATEGORIES.all.id &&
        !configs.groupAllByCategories)) &&
    !isPromotedCategory && // in case of it is a promoted category, load more should not work, because that endpoint does not work correctly
    data.length < category.gamesCount;

  const loadMoreButtonAvailable =
    loadMoreFunctionalityAvailable &&
    computedConfigs.loadMoreButtonType === CasinoGamesLoadMoreBtnType.BUTTON;

  const drawGamesWithCarousel =
    !showLazyViewOnSingleCategory &&
    isLoadMoreButtonSlider &&
    data.length >= computedConfigs.gamesPerRow;

  const drawGamesFallback = !drawGamesWithCarousel && !isLoadMoreButtonLazyLoad;

  const gamesByCategoryClasses = {
    aspectRatio: {
      [`casino-container__${
        configs.aspectRatio && configs.aspectRatio.toString().replace('.', '-')
      }`]: configs.aspectRatio
    },
    arrows: {
      'x-casinoGamesByCategories__arrows':
        computedConfigs.categoryNameVisible &&
        !viewAllEnabled &&
        isLoadMoreButtonSlider &&
        isMobile()
    }
  };

  const isAlternativeTitle = configs.favorite?.useAlternativeTitle;
  const isFavoriteMultiView =
    customCategory?.category?.id === 'favorite' && isAlternativeTitle;

  const createCategoryTitle = () => {
    if (isFavoriteMultiView) {
      return t('casino.favoriteGames');
    } else if (customCategory.isSelected) {
      return customCategory?.category?.title;
    } else {
      return category.name;
    }
  };

  const loadMoreContainerStyles = useMemo(
    () => ({
      className: cc([
        'x-casinoGamesByCategories__games',
        {
          'x-casinoGamesByCategories__small':
            computedConfigs.gamesPerRow >= MIN_GAMES_PER_ROW
        }
      ]),
      style: {
        '--games-per-row': computedConfigs.gamesPerRow,
        overflow: 'unset'
      } as CSSProperties
    }),
    [computedConfigs.gamesPerRow]
  );

  const onViewAll: MouseEventHandler<HTMLDivElement> = () => {
    const redirectPathToCasino = SpringConfigs.PAGE_URLS.casino;
    const shouldRedirectToCasino = shouldRedirect(redirectPathToCasino);

    const redirectPathToLiveCasino = SpringConfigs.PAGE_URLS['live-casino'];
    const shouldRedirectToLiveCasino = shouldRedirect(redirectPathToLiveCasino);

    if (!shouldRedirectToCasino || !shouldRedirectToLiveCasino) {
      parentRef?.current?.scrollIntoView();

      history.push({
        pathname: `${mountPoint(location.pathname, false, true)}/${
          category.id
        }`,
        search: location.search
      });
    } else {
      const redirectPath = shouldRedirectToCasino
        ? redirectPathToCasino
        : redirectPathToLiveCasino;

      window.location.href = `${redirectPath}/${SpringConfigs.CASINO_MOUNT_PATH}/${category.id}`;
    }
  };

  const CasinoGameCard = useCallback(
    ({
      game,
      index
    }: Pick<
      ComponentProps<typeof CasinoGameCardComponent>,
      'game' | 'index'
    >) =>
      computedConfigs.layoutType === CasinoGamesLayoutType.CLASSIC ? (
        <CasinoGameCardComponent
          game={game}
          casinoPlayRealBtnSize={configs.casinoPlayRealBtnSize}
          casinoNameSize={configs.casinoNameSize}
          index={index}
        />
      ) : (
        <PersonalizedGameCard game={game} />
      ),
    [
      configs.casinoPlayRealBtnSize,
      configs.casinoNameSize,
      computedConfigs.layoutType
    ]
  );

  if (
    SpringConfigs.USER_PERSONALIZATION_ENABLED &&
    !favoriteProviders?.length &&
    !data.length
  ) {
    return null;
  }

  const dataLength = SpringConfigs.USER_PERSONALIZATION_ENABLED
    ? favoriteProviders?.length + data.length
    : data.length;

  if (!dataLength) {
    return null;
  }

  return (
    <>
      <div
        className={cc([
          'x-casinoGamesByCategories',
          {
            'x-casinoGamesByCategories__carousel': isLoadMoreButtonSlider,
            'x-casinoGamesByCategories__carousel-mobile': isMobile()
          },
          gamesByCategoryClasses.aspectRatio,
          gamesByCategoryClasses.arrows
        ])}
      >
        {computedConfigs.categoryNameVisible || category.id === 'favorite' ? (
          <div
            className={cc([
              'x-casinoGamesByCategories__categoryName',
              {
                'x-casinoGamesByCategories__categoryName--small': isMobile(),
                'x-casinoGamesByCategories__categoryName--alternativeTitle':
                  isAlternativeTitle
              }
            ])}
            data-testid={`${category?.name?.toLowerCase()}`}
          >
            {computedConfigs.categoryNameVisible && createCategoryTitle()}
            {category.id === 'favorite' ? (
              <div
                className={cc([
                  'x-casinoGamesByCategories__categoryName__clearAll',
                  {
                    'x-casinoGamesByCategories__categoryName__clearAll--mobile':
                      isMobile()
                  }
                ])}
                onClick={() => {
                  dispatch(clearAllFavorite('casino'));
                }}
                style={
                  SpringConfigs.IS_RTL
                    ? { marginRight: 'auto' }
                    : { marginLeft: 'auto' }
                }
              >
                {t('casino.clearAll')} ({category.gamesCount})
              </div>
            ) : (
              viewAllEnabled && (
                <div
                  className={cc([
                    'x-casinoGamesByCategories__categoryName__viewAll',
                    {
                      'x-casinoGamesByCategories__categoryName__viewAll--small':
                        isMobile()
                    }
                  ])}
                  onClick={onViewAll}
                >
                  {t('casino.viewAll')} ({category.gamesCount})
                </div>
              )
            )}
          </div>
        ) : null}
        {/* render games with CAROUSEL */}
        {drawGamesWithCarousel && (
          <CasinoGamesCarousel
            loadMoreFunctionalityAvailable={loadMoreFunctionalityAvailable}
            data={data}
            CasinoGameCard={CasinoGameCard}
            viewAllEnabled={viewAllEnabled}
            onViewAll={onViewAll}
            gamesCount={category.gamesCount}
          />
        )}
        {/* render games with LAZY LOAD */}
        {isLoadMoreButtonLazyLoad && (
          <GamesLazyLoad
            gamesCount={category.gamesCount}
            data={data}
            loadMoreContainerStyles={loadMoreContainerStyles}
            loadMoreFunctionalityAvailable={loadMoreFunctionalityAvailable}
            CasinoGameCard={CasinoGameCard}
          />
        )}
        {/* render games with LOAD MORE */}
        {drawGamesFallback && (
          <div
            className={loadMoreContainerStyles.className}
            style={loadMoreContainerStyles.style}
            ref={containerRef}
          >
            {data.map(game => (
              <div
                key={game.id}
                // onClick={() => {
                //   setMobileActiveCardCLickHandler(game.id);
                // }}
              >
                <CasinoGameCard game={game} />
              </div>
            ))}
          </div>
        )}
      </div>
      {loadMoreButtonAvailable && (
        <Suspense fallback={null}>
          <CasinoLoadMoreButton />
        </Suspense>
      )}
    </>
  );
};

export const GamesByCategory = memo(_GamesByCategory);
